import axiosMain from 'axios';

const { REACT_APP_GLIDE_PATH: glidePath = '' } = process.env;

// Axios instance
const axios = axiosMain.create({
  baseURL: glidePath,
});

export const auth = {
  preRegister: ({ mobile }) => axios.post('/auth/pre-register', { mobile }),
  register: ({
    name,
    mobile,
    email,
    password,
    referral = null,
    signedUpVia = null,
  }) => {
    if (referral) {
      return axios.post('/auth/register', {
        name,
        mobile,
        email,
        password,
        referral,
      });
    } else if (signedUpVia) {
      return axios.post('/auth/register', {
        name,
        mobile,
        email,
        password,
        signedUpVia,
      });
    } else {
      return axios.post('/auth/register', { name, mobile, email, password });
    }
  },
  postRegister: ({ token, otp }) =>
    axios.post('/auth/post-register', { token, otp }),
  resentOtp: ({ token }) => axios.post('/auth/resend-otp', { token }),
  login: () => axios.post('/auth/login-uri'),
  preLogin: ({ sessionId }) =>
    axios.post('/auth/login', {
      sessionId,
    }),
  preLoginAcceptConsent: ({ sessionId }) =>
    axios.put(`/auth/accept-consent?sessionId=${sessionId}`),
  forgetPassword: ({ email }) => axios.post('/auth/forget-password', { email }),
  forgetPasswordSms: ({ mobile }) =>
    axios.post('/auth/forget-password-sms', { mobile }),
  resetTokenCheck: ({ token }) => axios.get(`/auth/reset/?t=${token}`),
  resetPassword: ({ token, password }) =>
    axios.post(`/auth/reset/?t=${token}`, { password }),
  logout: () => axios.post('/auth/logout'),
};

const findBacktestById = (res, id) => {
  const backtest = res.data.data;

  const findBacktest = backtest.find(({ _id }) => _id === id) || null;

  return findBacktest;
};

export const backtests = {
  getBacktest: () => axios.get('/backtests'),
  createBacktest: (name) => axios.post('/backtests', { name }),
  createBacktestWithConfig: (name, configurations) =>
    axios.post('/backtests', { name, configurations }),
  getBacktestById: (id) =>
    axios.get('/backtests').then((res) => findBacktestById(res, id)),
  deleteBacktest: ({ id }) => axios.delete(`/backtests/${id}`),
  deleteBacktestRedux: (backtestId) => axios.delete(`/backtests/${backtestId}`),
  renameBacktest: (id, name) =>
    axios.put(`/backtests/${id}`, {
      name,
    }),
  saveBacktestConfig: (id, config) =>
    axios.put(`/backtests/${id}`, {
      configurations: config,
    }),
  runBacktest: (config) =>
    axios.post('/backtests/run', {
      configurations: config,
    }),
  showResult: (jobId) => axios.get(`/backtests/result/${jobId}`),
};

export const users = {
  getConfigurations: () =>
    axios.get('/users/me').then((res) => res.data.data.configurations),
  getUser: () => axios.get('/users/me'),
  setConfigurations: (data) =>
    axios.put('/users/me/configurations', { ...data }),
  changePassword: ({ currentPassword, newPassword, confirmPassword }) =>
    axios.put('/users/me/password', {
      currentPassword,
      newPassword,
      confirmPassword,
    }),
  changeName: ({ name }) =>
    axios.put('/users/me', {
      name,
    }),
  getMyReferrals: (page = 1) => axios.get(`/users/me/referrals?page=${page}`),
  setAddress: (data) => axios.put('/users/me/address', data),
  setGst: (data) => axios.put('/users/me/gst-details', data),
  setEmail: (data) => axios.put('/users/me/email', data),
  setMobile: (data) => axios.put('/users/me/mobile', data),
};

export const brokers = {
  getBrokers: () => axios.get('/brokers'),
  getBrokersList: () => axios.get('/brokers/list'),
  removeBroker: (id) => axios.delete(`/brokers/${id}`),
  enableBroker: (id) => axios.put(`/brokers/${id}/enable`),
  clientWiseSetTarget: (brokerId, data) =>
    axios.post(`/brokers/${brokerId}/config`, data),
  getBrokerLoginUrls: () => axios.get('/brokers/login-uris'),
};

export const brokerSetup = {
  getAppUris: (broker, clientId) =>
    axios.get(`/brokers/${broker}/app-uris/${clientId}`),
  saveBrokerConfig: (broker, data = {}) =>
    axios.post(`/brokers/${broker}`, data),
};

export const brokerLogin = {
  siteLogin: (broker, clientId, data) =>
    axios.post(`/brokers/${broker}/login/${clientId}`, data),
  loginWithData: (broker, clientId, data = {}) =>
    axios.post(`/brokers/${broker}/login/${clientId}`, data),
};

const generateCreateAlgoLink = ({
  name,
  code,
  day,
  broker = '',
  configurations,
  type,
}) => {
  const axiosContent = {
    code,
    name,
    day,
  };

  if (broker && broker.trim() !== '') {
    axiosContent.broker = broker;
  }

  if (configurations) {
    axiosContent.configurations = configurations;
  }

  if (type) {
    axiosContent.type = type;
  }

  return axios.post('/algos', axiosContent);
};

export const algos = {
  getAlgos: () => axios.get('/algos'),
  getAlgosDayWise: () => axios.get('/algos/day-wise'),
  getAlgosConfig: () => axios.get('/algos/config'),
  getAlgoById: (id) => axios.get(`/algos/${id}`),
  createAlgo: ({ name, code, day, broker = '', configurations, type }) =>
    generateCreateAlgoLink({ name, code, day, broker, configurations, type }),
  deleteAlgo: (id) => axios.delete(`/algos/${id}`),
  renameAlgo: (id, name) =>
    axios.put(`/algos/${id}`, {
      name,
    }),
  duplicateAlgo: (id, name, day, broker = '', type = undefined) => {
    const data = {
      cloneOf: id,
      name,
      day,
    };

    if (broker && broker.trim() !== '') {
      data.broker = broker;
    }

    if (type) {
      data.type = type;
    }

    return axios.post(`/algos/clone`, data);
  },
  saveAlgoConfig: (id, configurations) =>
    axios.put(`/algos/${id}`, {
      configurations,
    }),
  updateBroker: (id, broker) =>
    axios.put(`/algos/${id}`, {
      broker,
    }),
  toggleAlgo: (id, toggleCondition = 'disable') =>
    axios.put(`/algos/${id}/${toggleCondition}`),
  sqOffAll: (id) => axios.put(`/algos/${id}/action/SQ_OFF_TERMINATE`),
  terminate: (id) => axios.put(`/algos/${id}/action/TERMINATE`),
  getAlgosTypes: () => axios.get('/algos/types'),
  bulkActionClone: (config = {}) =>
    axios.put('/algos/bulk/action/clone', config),
  bulkActionTerminate: (algoIds = []) =>
    axios.put('/algos/bulk/action/TERMINATE', { algoIds }),
  bulkActionSquareOffTerminate: (algoIds = []) =>
    axios.put('/algos/bulk/action/SQ_OFF_TERMINATE', { algoIds }),
  bulkActionDelete: (algoIds = []) =>
    axios.delete('/algos/bulk/action/delete', { data: { algoIds } }),
  clientWiseTerminate: (brokerId) =>
    axios.put(`/algos/broker/${brokerId}/action/TERMINATE`),
  clientWiseSqOffTerminate: (brokerId) =>
    axios.put(`/algos/broker/${brokerId}/action/SQ_OFF_TERMINATE`),
  exitSelectedLegs: ({ algoId, positions = '' }) =>
    axios.put(`/algos/${algoId}/positions/${positions}/action/SQ_OFF_POSITION`),
  bulkActionEnable: (algoIds = []) =>
    axios.put('/algos/bulk/action/enable', { algoIds }),
  bulkActionDisable: (algoIds = []) =>
    axios.put('/algos/bulk/action/disable', { algoIds }),
  retrySelectedLegs: ({
    algoId,
    positions = '',
    positionData,
    action = 'RETRY_ENTRY',
  }) =>
    axios.put(`/algos/${algoId}/positions/${positions}/action/${action}`, {
      positionData,
    }),
  resetAlgo: (id) => axios.put(`/algos/${id}/action/RESET_ALGO`),
  updateMultiplier: (id, multiplier) =>
    axios.put('/algos/bulk/action/edit', {
      algoIds: [id],
      multiplier,
    }),
  bulkUpdateMultiplier: (algoIds = [], multiplier) =>
    axios.put('/algos/bulk/action/edit', {
      algoIds,
      multiplier,
    }),
  bulkUpdateMTMModify: (algoIds = [], action, factor) =>
    axios.put('/algos/bulk/action/mtm-edit', {
      algoIds,
      action,
      factor,
    }),
  getAlgosDayWiseTypeSignal: () => axios.get('/algos/day-wise?type=SIGNAL'),
};

export const algoStatus = {
  getStatus: (id) => axios.get(`/algos/${id}/status`),
};

export const masterContracts = {
  getMasterContractsChecksum: () => axios.head('/master-contracts'),
  getMasterContracts: () => axios.get('/master-contracts'),
};

export const setAuthorizationHeader = (token = null) => {
  if (token) {
    axios.defaults.headers.common.authorization = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common.authorization;
  }
};

export const paymentGetways = {
  razorpayOrders: (amount) =>
    axios.post('/payment-gateways/razorpay/orders', { amount }),
  razorpayConfig: () => axios.get('/payment-gateways/razorpay/config'),
  razorpayCommit: ({ razorpayPaymentId, razorpayOrderId, razorpaySignature }) =>
    axios.post('/payment-gateways/razorpay/commit', {
      razorpayPaymentId,
      razorpayOrderId,
      razorpaySignature,
    }),
  razorPayStatus: (id) => axios.get(`/payment-gateways/razorpay/status/${id}`),
};

export const credits = {
  getCredits: (page = 1) => axios.get(`/credits?page=${page}`),
  getPaymentInvoice: (invoiceNumber) =>
    axios.get(`/credits/invoice?invoiceNumber=${invoiceNumber}`, {
      responseType: 'blob',
    }),
};

export const subscription = {
  getCoupons: (amount = '') => axios.get(`/coupons?amount=${amount}`),
  getPlans: () => axios.get('/subscriptions/plans'),
  getCheckoutPreviewNew: (data = {}) =>
    axios.post('/subscriptions/preview-new', data),
  subscribePlan: (data = {}) => axios.post('/subscriptions', data),
  subscribePlanAutoRenew: ({ id, autoRenew = false }) =>
    axios.put(`/subscriptions/${id}/set-auto-renew`, { autoRenew }),
  getCheckoutPreviewEdit: (id, data = {}) =>
    axios.post(`/subscriptions/${id}/preview-upcoming`, data),
  subscribePlanEdit: (id, data = {}) => axios.put(`/subscriptions/${id}`, data),
};

export const reports = {
  downloadTradeReport: (data = {}) =>
    axios.post('/reports/download/trade-report', data, {
      responseType: 'blob',
    }),
};

export const announcements = {
  getAnnouncements: () => axios.get('/announcements/active'),
};

export const botNotifications = {
  getBotNotificationSettings: () => axios.get('/users/me/notifications'),
  updateNotificationConfig: (enabled = false, medium) => {
    const data = {
      enabled,
    };

    if (medium) {
      data.medium = medium;
    }

    return axios.put('/users/me/notifications', data);
  },
  telegramSaveToken: (token) =>
    axios.put('/users/me/notifications/telegram', {
      token,
    }),
  telegramDelink: () => axios.delete('/users/me/notifications/telegram'),
};

export const signupPageNames = {
  getSignupPageName: (referral) => {
    if (referral?.field === 'referral') {
      return axios.head(`/signup-page-names?referral=${referral.value}`);
    } else if (referral?.field === 'signedUpVia') {
      return axios.head(`/signup-page-names?signedUpVia=${referral.value}`);
    }

    return axios.head(`/signup-page-names`);
  },
};

function getChartOhlcAccountLevelUrl({ fromDate, toDate, after = null }) {
  let basicUrl = `?graphLevel=account&from=${fromDate}&to=${toDate}`;

  if (after) {
    basicUrl += `&after=${after}`;
  }

  return basicUrl;
}

function getChartOhlcBrokerLevelUrl({
  broker = [],
  fromDate,
  toDate,
  after = null,
}) {
  let basicUrl = ``;

  basicUrl = `?graphLevel=broker`;

  basicUrl += `&from=${fromDate}&to=${toDate}`;

  for (let index = 0; index < broker.length; index++) {
    const brokerItem = broker[index];

    basicUrl += `&broker=${brokerItem}`;
  }

  if (after) {
    basicUrl += `&after=${after}`;
  }

  return basicUrl;
}

function getChartOhlcAlgoLevelUrl({
  algoIds = [],
  fromDate,
  toDate,
  after = null,
}) {
  let basicUrl = `?graphLevel=algo&from=${fromDate}&to=${toDate}`;

  for (let index = 0; index < algoIds.length; index++) {
    const algoId = algoIds[index];

    basicUrl += `&algoIds[]=${algoId}`;
  }

  if (after) {
    basicUrl += `&after=${after}`;
  }

  return basicUrl;
}

function getChartUnderlyingOhlcUrl({
  underlying,
  fromDate,
  toDate,
  after = null,
}) {
  let basicUrl = `/pnl-chart/underlying?from=${fromDate}&to=${toDate}&underlyingName=${underlying}`;

  if (after) {
    return `${basicUrl}&after=${after}`;
  }

  return basicUrl;
}

export const chartOhlc = {
  getAccountLevelData: (fromDate, toDate, after = null) => {
    const url = getChartOhlcAccountLevelUrl({ fromDate, toDate, after });

    return axios.get(`/pnl-chart/algo${url}`);
  },
  getBrokerLevelData: (broker, fromDate, toDate, after = null) => {
    const url = getChartOhlcBrokerLevelUrl({
      broker,
      fromDate,
      toDate,
      after,
    });

    return axios.get(`/pnl-chart/algo${url}`);
  },
  getAlgoLevelData: (algoIds, fromDate, toDate, after = null) => {
    const url = getChartOhlcAlgoLevelUrl({
      algoIds,
      fromDate,
      toDate,
      after,
    });

    return axios.get(`/pnl-chart/algo${url}`);
  },
  getUnderlyingData: (underlying, fromDate, toDate, after = null) =>
    axios.get(
      getChartUnderlyingOhlcUrl({
        underlying,
        fromDate,
        toDate,
        after,
      })
    ),
  getAccountLevelShareLink: (
    fromDate,
    toDate,
    chartType,
    showAccountName = false
  ) => {
    return axios.post(`/pnl-chart/algo/shareable-link`, {
      from: fromDate,
      to: toDate,
      graphLevel: 'account',
      chartType: chartType === 'c' ? 'candlestick' : 'line',
      splittedGraph: false,
      showAccountName,
    });
  },
  getClientIdLevelShareLink: (
    clientIds,
    fromDate,
    toDate,
    chartType,
    showAccountName = false
  ) => {
    return axios.post(`/pnl-chart/algo/shareable-link`, {
      from: fromDate,
      to: toDate,
      graphLevel: 'broker',
      chartType: chartType === 'c' ? 'candlestick' : 'line',
      splittedGraph: false,
      broker: clientIds[0],
      showAccountName,
    });
  },
  getAlgoLevelShareLink: (
    algoIds,
    fromDate,
    toDate,
    chartType,
    showAccountName = false
  ) => {
    return axios.post(`/pnl-chart/algo/shareable-link`, {
      from: fromDate,
      to: toDate,
      graphLevel: 'algo',
      chartType: chartType === 'c' ? 'candlestick' : 'line',
      splittedGraph: false,
      algoIds,
      showAccountName,
    });
  },
};

export const signals = {
  signalGroups: (type = 'tv', page = 1) =>
    axios.get(`/signal-groups?type=${type}&page=${page}`),
  createSignalGroup: ({ name, type, expiry, maxEntryCount }) =>
    axios.post('/signal-groups', {
      name,
      type,
      expiry,
      maxEntryCount,
    }),
  toggleSignal: (id, toggleCondition = 'disable') =>
    axios.put(`/signal-groups/${id}/${toggleCondition}`),
  deleteSignal: (id) => axios.delete(`/signal-groups/${id}`),
  signalGroupById: (id) => axios.get(`/signal-groups/${id}`),
  renameSignal: (id, name) => axios.put(`/signal-groups/${id}`, { name }),
  updateExpiry: (id, expiry = null) =>
    axios.put(`/signal-groups/${id}`, { expiry }),
  signalAlerts: (signalGroupId) =>
    axios.get(`/signal-groups/${signalGroupId}/signals`),
  signalAlertById: (signalGroupId, alertId) =>
    axios.get(`/signal-groups/${signalGroupId}/signals/${alertId}`),
  createSignalAlert: (signalGroupId, name, maxEntryCount) =>
    axios.post(`/signal-groups/${signalGroupId}/signals`, {
      name,
      maxEntryCount,
    }),
  updateSignalAlert: (signalGroupId, alertId, { name, algos, maxEntryCount }) =>
    axios.put(`/signal-groups/${signalGroupId}/signals/${alertId}`, {
      name,
      algos,
      maxEntryCount,
    }),
  deleteSignalAlert: (signalGroupId, alertId) =>
    axios.delete(`/signal-groups/${signalGroupId}/signals/${alertId}`),
};

export const userOrderBook = {
  checkActiveRateLimit: () => axios.head('algos/order-book'),
  orderBook: (brokers = []) => axios.post(`algos/order-book`, { brokers }),
};

export const userMargin = {
  checkActiveRateLimit: () => axios.head('algos/margin'),
  margin: (brokers = []) => axios.post(`algos/margin`, { brokers }),
};

export const notificationAlerts = {
  getNotificationAlerts: (page = 1) =>
    axios.get(`/announcements/?page=${page}`),
  markNotificationAsRead: (seqIds = []) =>
    axios.post(`/announcements/mark-as-read`, {
      seqIds,
    }),
};

export const algoBulkErrors = {
  getAlgoBulkErrors: () => axios.get('/algos/error-legs'),
  retrySelectedLegs: (algos) =>
    axios.put('/algos/positions/bulk/retry', { algos }),
};
